/* global BigInt */
import { DateTime } from "luxon";
import { mileagePayout, resolveMileage } from "./shootMileage";
import { parsePayPeriod, payPeriodLabel, checkDateLabel } from "./payPeriods";

export const SALES_USERS = ["rob@vsnmichigan.com", "victoria@vsnmichigan.com"];
export const SALES_START_DATE = "2026-09-20";
export const canAccessSales = user => SALES_USERS.includes(String(user?.email || "").trim().toLowerCase());
export const salesDay = value => {
  const date = DateTime.fromISO(value || "").setZone("America/New_York");
  return date.isValid ? date.toISODate() : "";
};
export const crewFor = appointment => [...new Set((Array.isArray(appointment.photographers) ? appointment.photographers : [])
  .filter(name => typeof name === "string" && name.trim() && name !== "Unassigned"))];
export const normalizeSale = row => {
  const payments = row.payments || [];
  const legacy = typeof row.paid !== "boolean";
  const paidCount = payments.filter(person => person.paid).length;
  return {...row, payments, paid:legacy ? payments.length > 0 && paidCount === payments.length : row.paid,
    payPeriod:parsePayPeriod(row.payPeriod)?.start || row.payPeriod || "", legacyPartial:legacy && paidCount > 0 && paidCount < payments.length};
};
export const paymentStatus = row => normalizeSale(row).paid ? "Paid" : "Not paid";
export const isReadyToPay = row => row.salesCents != null && !normalizeSale(row).paid;
export const COMMISSION_SETTINGS_ID = "commission-settings";
export const MINIMUM_PAYOUT_CENTS = 4000;
export const PAYOUT_TYPES = {standard:"Commission + mileage",salaryWeekend:"Salary - weekend commission + mileage",weekend:"Weekend commission - no mileage",salary:"Salary - mileage only (previous policy)"};
export const defaultCommissionRate = (defaults,name) => {
  const value=defaults[name];
  return value && typeof value==="object" ? value.commissionRate : value;
};
export function defaultPayoutType(defaults,name) {
  if(!Object.prototype.hasOwnProperty.call(defaults,name))return null;
  const configured=defaults[name]?.payoutType || "standard";
  const type=configured==="salary" ? "salaryWeekend" : configured;
  if(!Object.prototype.hasOwnProperty.call(PAYOUT_TYPES,type))throw new Error(`Invalid default payout type for ${name}.`);
  return type;
}
export function payoutTypeFor(row,person,defaults={}) {
  if(person.payoutType != null)return Object.prototype.hasOwnProperty.call(PAYOUT_TYPES,person.payoutType) ? person.payoutType : null;
  // Legacy saved records keep the standard policy they were calculated with.
  if(row.revision>0)return "standard";
  const configured=defaults[person.nickname]?.payoutType || "standard";
  const type=configured==="salary" ? "salaryWeekend" : configured;
  return Object.prototype.hasOwnProperty.call(PAYOUT_TYPES,type) ? type : null;
}
export function parseCommissionRate(value) {
  if (value === "" || value == null) return null;
  const text = String(value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text) || Number(text)<11 || Number(text)>30) throw new Error("Commission must be between 11% and 30%, with up to two decimal places.");
  return Number(text);
}
export function snapshotCommissions(row, defaults) {
  return {...row, payments:row.payments.map(person => {
    const payoutType=payoutTypeFor(row,person,defaults);
    if(!payoutType)throw new Error(`Choose a payout type for ${person.nickname}.`);
    const extraCents=person.extraCents ?? 0;
    if(!Number.isSafeInteger(extraCents) || extraCents<0)throw new Error(`Enter a valid extra payment for ${person.nickname}.`);
    if(extraCents>0 && !String(person.extraReason || "").trim())throw new Error(`Enter a reason for ${person.nickname}'s extra payment.`);
    return {...person,payoutType,extraCents,extraReason:String(person.extraReason || "").trim(),
      commissionRate:parseCommissionRate(Object.prototype.hasOwnProperty.call(person,"commissionRate") ? person.commissionRate : defaultCommissionRate(defaults,person.nickname))};
  })};
}
export const commissionSplitCount = row => row.payments.filter(person=>person.excludeFromCommission !== true).length;
export function commissionBreakdown(row, defaults = {}) {
  const people = row.payments.map(person => {
    let rate = null;
    try {rate = parseCommissionRate(Object.prototype.hasOwnProperty.call(person,"commissionRate") ? person.commissionRate : defaultCommissionRate(defaults,person.nickname));} catch { /* Incomplete editor values have no payout yet. */ }
    return {...person,commissionRate:rate};
  });
  const splitCount=commissionSplitCount(row);
  return people.map(person => {
    const payoutType=payoutTypeFor(row,person,defaults);
    const day=DateTime.fromISO(salesDay(row.appointmentDate),{zone:"America/New_York"});
    const weekend=day.isValid && day.weekday>=6;
    const weekendCommission=payoutType==="weekend" || payoutType==="salaryWeekend";
    const excluded=person.excludeFromCommission===true;
    const earnsCommission=!excluded && (payoutType==="standard" || (weekendCommission && weekend));
    let payoutCents = excluded || payoutType==="salary" || (weekendCommission && day.isValid && !weekend) ? 0 : null;
    if (earnsCommission && row.salesCents != null && splitCount && person.commissionRate != null) {
      // Divide and apply the rate before rounding; never round the sales share first.
      const numerator = BigInt(row.salesCents) * BigInt(Math.round(person.commissionRate * 100));
      const denominator = BigInt(splitCount * 10000);
      payoutCents = Math.max(payoutType==="weekend" ? 25000 : MINIMUM_PAYOUT_CENTS, Number((numerator + denominator / 2n) / denominator));
    }
    const mileageCents = payoutType==="weekend" ? 0 : payoutType ? mileagePayout(row) : null;
    const extraCents=person.extraCents ?? 0;
    const validExtra=Number.isSafeInteger(extraCents) && extraCents>=0 && (!extraCents || String(person.extraReason || "").trim());
    return {...person,payoutType,payoutCents,mileageCents,extraCents,
      totalPayoutCents:payoutCents == null || mileageCents == null || !validExtra ? null : payoutCents+mileageCents+extraCents};
  });
}
export function commissionSummary(rows, defaults = {}) {
  const totals = new Map();
  rows.forEach(row => commissionBreakdown(row,defaults).forEach(person => {
    const total = totals.get(person.nickname) || {nickname:person.nickname,payoutCents:0,commissionCents:0,mileageCents:0,extraCents:0,shoots:0,missing:0};
    total.shoots++;total.payoutCents += person.totalPayoutCents || 0;
    total.commissionCents+=person.payoutCents || 0;total.mileageCents+=person.mileageCents || 0;
    total.extraCents+=Number.isSafeInteger(person.extraCents) ? person.extraCents : 0;
    if (person.totalPayoutCents == null) total.missing++;
    totals.set(person.nickname,total);
  }));
  return [...totals.values()].sort((a,b) => a.nickname.localeCompare(b.nickname));
}
export function commissionCSV(rows, defaults = {}) {
  const data = [["Appointment ID","Shoot date","Client / job","Pay period","Payment status","Photographer","Shoot sales","Crew size","Commission percent","Commission payout","Client ID","Miles","Rate per mile","Mileage payout","Total payout","Calculation status","Check date","Payout type","Extra pay","Extra reason","Commission split count","Excluded from commission"]];
  rows.forEach(row => commissionBreakdown(row,defaults).forEach(person => data.push([
    row.id,salesDay(row.appointmentDate),row.client,payPeriodLabel(row.payPeriod),paymentStatus(row),person.nickname,
    row.salesCents == null ? "" : (row.salesCents/100).toFixed(2),row.payments.length,
    person.commissionRate ?? "",person.payoutCents == null ? "" : (person.payoutCents/100).toFixed(2),
    resolveMileage(row).clientID,resolveMileage(row).miles ?? "",resolveMileage(row).ratePerMile ?? "",
    person.mileageCents == null ? "" : (person.mileageCents/100).toFixed(2),
    person.totalPayoutCents == null ? "" : (person.totalPayoutCents/100).toFixed(2),
    person.totalPayoutCents == null ? "Incomplete required payout data" : "Calculated",
    checkDateLabel(row.payPeriod),
    PAYOUT_TYPES[person.payoutType] || "Unknown",(person.extraCents/100).toFixed(2),person.extraReason || "",commissionSplitCount(row),person.excludeFromCommission===true ? "Yes" : "No",
  ])));
  return encodeSalesCSV(data);
}
export function salesPayload(row, uid, timestamp) {
  const period=parsePayPeriod(row.payPeriod);
  if(row.payPeriod && !period)throw new Error("Choose a standard biweekly pay period. Previous free-text entries must be replaced before saving.");
  return {appointmentId:row.id, client:row.client, appointmentDate:row.appointmentDate,
    payments:row.payments, paid:row.paid, payPeriod:period?.start || "",
    salesCents:row.salesCents, notes:row.notes, revision:(row.revision || 0)+1,
    clientID:row.clientID || "", mileage:row.mileage || null,
    updatedBy:uid, updatedAt:timestamp};
}
export function batchPaymentChange(row, action, value) {
  if(action === "status") {
    if(typeof value !== "boolean")throw new Error("Choose Paid or Not paid.");
    return {...row,paid:value,legacyPartial:false};
  }
  if(action === "clearPeriod")return {...row,payPeriod:""};
  if(action === "payoutTypes") {
    return {...row,payments:row.payments.map(person=>{
      const payoutType=defaultPayoutType(value,person.nickname);
      return payoutType==null ? person : {...person,payoutType};
    })};
  }
  if(action === "commissions") {
    const payments=row.payments.map(person=>{
      const rate=parseCommissionRate(defaultCommissionRate(value,person.nickname));
      return rate==null ? person : {...person,commissionRate:rate};
    });
    return {...row,payments};
  }
  if(action === "mileage") {
    const mileage=resolveMileage({...row,clientID:row.clientID || row.mileage?.clientID || "",mileage:null},value);
    if(mileage.miles==null || mileage.ratePerMile==null)throw new Error(`No current mileage match or rate for ${row.client || row.id}.`);
    return {...row,mileage};
  }
  if(action === "period") {
    const period=parsePayPeriod(value);
    if(!period)throw new Error("Choose a standard pay period to assign.");
    return {...row,payPeriod:period.start};
  }
  throw new Error("Choose one batch action.");
}
export async function commitPaymentBatch(rows, action, value, {db,uid,doc,runTransaction,serverTimestamp}) {
  if(!rows.length || rows.length>100)throw new Error("Select between 1 and 100 shoots per update.");
  const changes=rows.map(row=>({row:batchPaymentChange(row,action,value),reference:doc(db,"shoot_sales",row.id)}));
  await runTransaction(db,async transaction=>{
    const snapshots=await Promise.all(changes.map(change=>transaction.get(change.reference)));
    snapshots.forEach((snapshot,index)=>{
      if((snapshot.exists()?snapshot.data().revision || 0:0)!==(changes[index].row.revision || 0))throw new Error("A selected shoot changed in another session. Nothing was saved. Reload before trying again.");
    });
    changes.forEach(({row,reference},index)=>{
      const audit={revision:(row.revision || 0)+1,updatedBy:uid,updatedAt:serverTimestamp()};
      if(snapshots[index].exists())transaction.update(reference,{...audit,...(action==="status" ? {paid:row.paid} : action==="mileage" ? {mileage:row.mileage} : ["commissions","payoutTypes"].includes(action) ? {payments:row.payments} : {payPeriod:row.payPeriod})});
      else transaction.set(reference,salesPayload(row,uid,audit.updatedAt));
    });
  });
  return changes.map(({row})=>({...row,revision:(row.revision || 0)+1}));
}
export async function commitSalesRows(rows, {db, uid, doc, runTransaction, serverTimestamp}) {
  if (!rows.length || rows.length > 100) throw new Error("Select between 1 and 100 shoots per update.");
  const changes = rows.map(row => ({row, reference:doc(db,"shoot_sales",row.id), payload:salesPayload(row,uid,serverTimestamp())}));
  await runTransaction(db, async transaction => {
    // Firestore transactions require all reads before any writes.
    const snapshots = await Promise.all(changes.map(change => transaction.get(change.reference)));
    snapshots.forEach((snapshot, index) => {
      if ((snapshot.exists() ? snapshot.data().revision || 0 : 0) !== (changes[index].row.revision || 0)) {
        throw new Error("A selected shoot changed in another session. Nothing was saved. Reload before trying again.");
      }
    });
    changes.forEach(change => transaction.set(change.reference,change.payload));
  });
  return changes.map(({row,payload}) => ({...row, payPeriod:payload.payPeriod, revision:payload.revision,legacyPartial:false}));
}
export function parseSalesCents(value) {
  const text = String(value).trim();
  if (!text) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) throw new Error("Enter a nonnegative sales amount with up to two decimal places.");
  const [whole, fraction = ""] = text.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) throw new Error("Sales amount is too large.");
  return cents;
}
function encodeSalesCSV(data) {
  // Prevent spreadsheet formulas in text cells, including leading whitespace.
  const cell = value => {
    let text = String(value ?? "");
    if (/^\s*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)) text = "'" + text;
    return '"' + text.replace(/"/g, '""') + '"';
  };
  return "\uFEFF" + data.map(row => row.map(cell).join(",")).join("\r\n");
}
export function salesTally(rows) {
  const entered = rows.filter(row => row.salesCents != null);
  return {totalCents:entered.reduce((sum,row) => sum + row.salesCents,0),
    shoots:rows.length, entered:entered.length, missing:rows.length-entered.length};
}
export function salesCSV(rows, filterDescription = "", defaults = {}) {
  const data = [["Appointment ID", "Shoot date", "Client / job", "Photographers", "Sales", "Photographer payment status", "Pay period", "Notes", "Client ID", "Miles", "Rate per mile", "Mileage lookup amount", "Total crew mileage", "Check date", "Crew extras", "Extra payment details", "Crew total payout"]];
  rows.forEach(row => data.push([row.id, salesDay(row.appointmentDate), row.client,
    (row.payments || []).map(person => person.nickname).join("; "),
    row.salesCents == null ? "" : (row.salesCents / 100).toFixed(2), paymentStatus(row),
    payPeriodLabel(row.payPeriod), row.notes || "",resolveMileage(row).clientID,resolveMileage(row).miles ?? "",resolveMileage(row).ratePerMile ?? "",
    mileagePayout(row)==null ? "" : (mileagePayout(row)/100).toFixed(2),commissionBreakdown(row,defaults).some(person=>person.mileageCents==null) ? "" : (commissionBreakdown(row,defaults).reduce((sum,person)=>sum+person.mileageCents,0)/100).toFixed(2),checkDateLabel(row.payPeriod),
    (commissionBreakdown(row,defaults).reduce((sum,person)=>sum+(person.extraCents || 0),0)/100).toFixed(2),
    row.payments.filter(person=>person.extraCents>0).map(person=>`${person.nickname}: ${(person.extraCents/100).toFixed(2)} - ${person.extraReason || ""}`).join("; "),
    commissionBreakdown(row,defaults).some(person=>person.totalPayoutCents==null) ? "" : (commissionBreakdown(row,defaults).reduce((sum,person)=>sum+person.totalPayoutCents,0)/100).toFixed(2)]));
  const tally = salesTally(rows);
  data.push(["", "", "TOTAL SALES", "", (tally.totalCents/100).toFixed(2), "", "", `${tally.entered} shoots with sales entered; ${tally.missing} missing sales`,"","","","",""]);
  data.push(["", "", "MATCHING SHOOTS", "", "", "", "", String(tally.shoots),"","","","",""]);
  const mileageTotal=rows.reduce((sum,row)=>sum+commissionBreakdown(row,defaults).reduce((subtotal,person)=>subtotal+(person.mileageCents || 0),0),0);
  data.push(["", "", "TOTAL CREW MILEAGE", "", "", "", "", "Excludes missing mileage calculations","","","","",(mileageTotal/100).toFixed(2)]);
  if (filterDescription) data.push(["", "", "REPORT FILTERS", "", "", "", "", filterDescription,"","","","",""]);
  return encodeSalesCSV(data.map(values=>[...values,...Array(Math.max(0,data[0].length-values.length)).fill("")]));
}

// Appointment crew is authoritative only while a financial record remains unpaid.
export function syncUnpaidCrew(row,appointment,defaults={}) {
  if(!appointment || normalizeSale(row).paid)return row;
  const names=crewFor(appointment),existing=new Map(row.payments.map(person=>[person.nickname,person]));
  if(names.length===existing.size && names.every(name=>existing.has(name)))return row;
  const payments=names.map(nickname=>existing.get(nickname) || snapshotCommissions({revision:0,payments:[{nickname}]},defaults).payments[0]);
  return {...row,payments};
}
export async function syncUnpaidCrewRecord(id,{db,uid,doc,runTransaction,serverTimestamp}) {
  return runTransaction(db,async transaction=>{
    const reference=doc(db,"shoot_sales",id);
    const [sale,appointment,settings]=await Promise.all([transaction.get(reference),transaction.get(doc(db,"appointments",id)),transaction.get(doc(db,"shoot_sales",COMMISSION_SETTINGS_ID))]);
    if(!sale.exists())return {row:null,changed:false};
    const row=normalizeSale({...sale.data(),id});
    const updated=syncUnpaidCrew(row,appointment.exists() ? appointment.data() : null,settings.exists() ? settings.data().rates || {} : {});
    if(updated===row)return {row,changed:false};
    const revision=(row.revision || 0)+1;
    transaction.update(reference,{payments:updated.payments,revision,updatedBy:uid,updatedAt:serverTimestamp()});
    return {row:{...updated,revision},changed:true};
  });
}

export const includeSalesRecord = (row,appointmentExists) => appointmentExists || normalizeSale(row).paid;
